// Input controls for the Current-state section on FeatureDetail.
//
//   SimpleDateInput / DateToggle — concrete calendar dates. Used for the EA
//                          transition date.
//
//   GaTargetInput        — 5-way (Not set | TBD | Quarter | Week | Date).
//                          The single GA target: a concrete date OR a coarse
//                          forward projection, encoded into `ga_date`.
import {
  formatProjectedGa,
  type ProjectedGa,
  type ProjectedGaKind,
} from "@/lib/projectedGa";

// ─── Simple date picker (EA/GA transition dates) ─────────────────────

export function SimpleDateInput({
  value,
  onChange,
  label,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  label?: string;
}) {
  const mode: "unset" | "date" = value == null ? "unset" : "date";
  return (
    <div className="space-y-2">
      <div className="inline-flex rounded-md border border-border-hover bg-bg p-0.5">
        <ModeButton
          active={mode === "unset"}
          onClick={() => onChange(null)}
          label="Not set"
        />
        <ModeButton
          active={mode === "date"}
          onClick={() => {
            // Default to today when flipping to "Date" from "unset".
            if (value == null) onChange(new Date().toISOString().slice(0, 16));
          }}
          label={label ?? "Date"}
        />
      </div>
      {mode === "date" && (
        <input
          type="datetime-local"
          value={(value ?? "").slice(0, 16)}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputCls + " max-w-[240px]"}
        />
      )}
    </div>
  );
}

// ─── Projected GA target picker (5-way) ──────────────────────────────

const QUARTER_OPTIONS = generateQuarterOptions();

/** GA-target picker (5-way: Not set | TBD | Quarter | Week | Date). Encodes
 *  into the single `ga_date` field via lib/projectedGa.ts — a concrete date
 *  is a real GA milestone, a coarse value is a forward projection. */
export function GaTargetInput({
  value,
  onChange,
}: {
  value: ProjectedGa;
  onChange: (v: ProjectedGa) => void;
}) {
  const setKind = (k: ProjectedGaKind) => {
    switch (k) {
      case "unset":
        onChange({ kind: "unset" });
        return;
      case "tbd":
        onChange({ kind: "tbd" });
        return;
      case "quarter":
        onChange({ kind: "quarter", value: currentQuarterLabel() });
        return;
      case "week":
        onChange({ kind: "week", value: currentIsoWeek() });
        return;
      case "date":
        onChange({
          kind: "date",
          value: new Date().toISOString().slice(0, 16),
        });
        return;
    }
  };

  return (
    <div className="space-y-2">
      <div className="inline-flex flex-wrap rounded-md border border-border-hover bg-bg p-0.5">
        {(
          [
            ["unset", "Not set"],
            ["tbd", "TBD"],
            ["quarter", "Quarter"],
            ["week", "Week"],
            ["date", "Date"],
          ] as [ProjectedGaKind, string][]
        ).map(([k, label]) => (
          <ModeButton
            key={k}
            active={value.kind === k}
            onClick={() => setKind(k)}
            label={label}
          />
        ))}
      </div>
      {value.kind === "quarter" && (
        <select
          value={value.value}
          onChange={(e) =>
            onChange({ kind: "quarter", value: e.target.value })
          }
          className={inputCls + " max-w-[240px]"}
        >
          {/* Include the current value even if it's outside the generated
              window, so an editor loading old data doesn't have their
              selection silently reset. */}
          {!QUARTER_OPTIONS.includes(value.value) && (
            <option value={value.value}>{value.value}</option>
          )}
          {QUARTER_OPTIONS.map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
        </select>
      )}
      {value.kind === "week" && (
        <div className="space-y-1">
          <input
            type="week"
            value={value.value}
            onChange={(e) => onChange({ kind: "week", value: e.target.value })}
            className={inputCls + " max-w-[240px]"}
          />
          {/* <input type="week"> shows only "YYYY-Www". Reveal the actual
              date (Monday of that ISO week) so editors don't have to
              translate the week number in their head. */}
          <p className="text-xs text-text-tertiary">
            {formatProjectedGa(`W:${value.value}`)}
          </p>
        </div>
      )}
      {value.kind === "date" && (
        <input
          type="datetime-local"
          value={value.value.slice(0, 16)}
          onChange={(e) => onChange({ kind: "date", value: e.target.value })}
          className={inputCls + " max-w-[240px]"}
        />
      )}
    </div>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────

const inputCls =
  "w-full rounded-md border border-border-hover bg-bg px-3 py-1.5 text-sm focus:border-accent focus:outline-none";

/** Checkbox-gated single date (YYYY-MM-DD). Unchecked = unset; checking
 *  defaults to today and reveals the date field + hint. Shared by the create
 *  wizard and the feature editor so the two read identically. */
export function DateToggle({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const enabled = value !== "";
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <input
        type="checkbox"
        className="h-3.5 w-3.5 accent-accent"
        checked={enabled}
        onChange={(e) =>
          onChange(e.target.checked ? new Date().toISOString().slice(0, 10) : "")
        }
        aria-label={`Set ${label}`}
      />
      <span className="w-16 text-text-secondary">{label}</span>
      {enabled && (
        <>
          <input
            type="date"
            value={value.slice(0, 10)}
            onChange={(e) => onChange(e.target.value)}
            className={inputCls + " max-w-[170px]"}
          />
          {hint && <span className="text-xs text-text-tertiary">{hint}</span>}
        </>
      )}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded-sm bg-card px-3 py-1 text-xs font-medium text-text shadow-sm"
          : "rounded-sm px-3 py-1 text-xs text-text-secondary hover:text-text"
      }
    >
      {label}
    </button>
  );
}

/** Current quarter + a few forward — e.g. ["Q3 2026", "Q4 2026", "Q1 2027", "Q2 2027"]. */
function generateQuarterOptions(): string[] {
  const now = new Date();
  const y = now.getUTCFullYear();
  const q = Math.floor(now.getUTCMonth() / 3) + 1;
  const out: string[] = [];
  for (let i = 0; i < 6; i++) {
    const qi = ((q - 1 + i) % 4) + 1;
    const yi = y + Math.floor((q - 1 + i) / 4);
    out.push(`Q${qi} ${yi}`);
  }
  return out;
}

function currentQuarterLabel(): string {
  const now = new Date();
  const q = Math.floor(now.getUTCMonth() / 3) + 1;
  return `Q${q} ${now.getUTCFullYear()}`;
}

/** Today's ISO week in "YYYY-Www" form, matching <input type="week">. */
function currentIsoWeek(): string {
  const now = new Date();
  // Copy and shift to Thursday of the same week (ISO 8601 anchor).
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const jan1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((+d - +jan1) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}
